import { useState } from "react"
import { router } from "@inertiajs/react"
import { toast } from "sonner"
import { track } from "@/lib/analytics"
import type { Video } from "@/types"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Input } from "@/components/ui/input"

type Role = "owner" | "admin" | "member" | string

export default function VideoActionsMenu({
  video,
  currentRole,
}: {
  video: Video
  currentRole: Role
}) {
  const canDelete = currentRole === "owner" || currentRole === "admin"
  const canRename = true
  const canDuplicate = true

  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [renameValue, setRenameValue] = useState(video.name)
  const [renaming, setRenaming] = useState(false)
  const [duplicating, setDuplicating] = useState(false)

  if (!canRename && !canDuplicate && !canDelete) return null

  function handleRenameSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === video.name) {
      setRenameOpen(false)
      return
    }
    setRenaming(true)
    router.patch(
      `/dashboard/videos/${video.id}`,
      { video: { name: trimmed } },
      {
        preserveScroll: true,
        onSuccess: () => {
          track("video_renamed", { video_id: video.id })
          setRenameOpen(false)
        },
        onError: () => {
          toast.error("Could not rename video")
        },
        onFinish: () => setRenaming(false),
      },
    )
  }

  function handleDuplicate() {
    setDuplicating(true)
    router.post(
      `/dashboard/videos/${video.id}/duplicate`,
      {},
      {
        onSuccess: () => {
          track("video_duplicated", { video_id: video.id })
        },
        onError: () => {
          toast.error("Could not duplicate video")
        },
        onFinish: () => setDuplicating(false),
      },
    )
  }

  function handleDelete() {
    track("video_deleted", { video_id: video.id })
    router.delete(`/dashboard/videos/${video.id}`)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Video actions"
          >
            <svg
              className="size-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.8}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
              />
            </svg>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canRename && (
            <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
              Rename
            </DropdownMenuItem>
          )}
          {canDuplicate && (
            <DropdownMenuItem
              onSelect={handleDuplicate}
              disabled={duplicating}
            >
              {duplicating ? "Duplicating…" : "Duplicate"}
            </DropdownMenuItem>
          )}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setDeleteOpen(true)}
                className="text-red focus:text-red"
              >
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <form onSubmit={handleRenameSubmit}>
            <DialogHeader>
              <DialogTitle>Rename video</DialogTitle>
              <DialogDescription>
                Give this video a name that makes sense to you and your team.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <Input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                maxLength={120}
              />
            </div>
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setRenameOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  renaming ||
                  !renameValue.trim() ||
                  renameValue.trim() === video.name
                }
              >
                {renaming ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete video?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this video and all its scripts. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
